// Display current user premium subscription details if subscribed
// If not subscribe serves as action call for getting premium subscription
import React, { useContext } from "react";
import { format, isValid, parse } from "date-fns";
import { toast } from "react-toastify";
import { Link, useHistory } from "react-router-dom";
import { Info, SketchLogo } from "phosphor-react";
import { UserContext } from "context/user";
import { StripeContext } from "context/stripe";
import usePremium from "hooks/usePremium";
import { getPricing } from "utils";
import { getStripePortal } from "api/User";
import Button from "components/Button";
import "./index.scss";

const PremiumStatus = () => {
  const history = useHistory();
  const { token, profile } = useContext(UserContext);
  const { state } = useContext(StripeContext);
  const { isPremium } = usePremium();

  const { pricingDetails, isFetchingPricingDetails } = state;

  const handleManagePaymentOptions = async (event) => {
    event.preventDefault();
    try {
      const response = await getStripePortal(token);
      if (response) {
        window.location.replace(response.portal_url, "_blank");
      }
    } catch (e) {
      toast.error(e);
    }
  };

  // Render premium subscription status
  const renderPremium = () => {
    // Adds handler if expires_at is null
    const expiresAt = parse(
      profile?.subscriptions?.expires_at,
      "MM/dd/yyyy, HH:mm:ss",
      new Date()
    );
    let expirationDate = "";
    if (isValid(expiresAt)) {
      expirationDate = format(new Date(expiresAt), "MMMM d, yyyy");
    } else {
      expirationDate = "N/A";
    }
    return (
      <div className="premium-wrapper">
        <p className="status">You are subscribed to Advanced</p>
        <p className="details">
          Your Advanced subscription will expire on {expirationDate}
        </p>
        <Button
          color="green"
          type="solid"
          label="Manage Subscription"
          block
          onClick={(e) => handleManagePaymentOptions(e)}
        />
      </div>
    );
  };

  // Render action call for getting premium subscription
  const renderBasic = () => {
    const { discountValue, discountValidity, taxRate, isValidDiscount } =
      getPricing(pricingDetails);
    let formattedDate = "";
    if (isValid(discountValidity)) {
      formattedDate = format(new Date(discountValidity), "MMMM");
    } else {
      formattedDate = "";
    }
    const hasOffer =
      !isFetchingPricingDetails &&
      (discountValue || taxRate) &&
      isValidDiscount;
    return (
      <div className="premium-wrapper">
        <p className="status">You are on a basic account</p>
        <p className="details">
          Bring your farm management to the next level by subscribing to
          Advanced features.
          <div className="link-wrapper">
            <Link className="link" to="/advanced">
              Learn more about Advanced.
            </Link>
            <Info size={20} color="#09ba51" />
          </div>
        </p>
        {hasOffer && (
          <div className="offer-letter">
            <p className="text">
              <span className="bold">Hey there! </span> We are offering a{" "}
              {discountValue} price discount for the Advanced subscription until{" "}
              {formattedDate}.
            </p>
            <div className="logo">
              <SketchLogo size={20} color="#09ba51" />
            </div>
          </div>
        )}

        <Button
          color="green"
          type="solid"
          label="Subscribe to Advanced"
          block
          onClick={() => history.push("/advanced/subscribe")}
        />
      </div>
    );
  };

  const renderCancelled = () => {
    const cancelledAt = parse(
      profile?.subscriptions?.cancelled_at,
      "MM/dd/yyyy, HH:mm:ss",
      new Date()
    );
    let cancelledDate = "";
    if (isValid(cancelledAt)) {
      cancelledDate = format(new Date(cancelledAt), "MMMM d, yyyy");
    } else {
      cancelledDate = "N/A";
    }
    const expiresAt = parse(
      profile?.subscriptions?.expires_at,
      "MM/dd/yyyy, HH:mm:ss",
      new Date()
    );

    let expirationDate = "";
    if (isValid(expiresAt)) {
      expirationDate = format(new Date(expiresAt), "MMMM d, yyyy");
    } else {
      expirationDate = "N/A";
    }
    return (
      <div className="premium-wrapper">
        <p className="status">
          Your subscription was cancelled on {cancelledDate}
        </p>
        <p className="details">
          Your Advanced subscription will expire on {expirationDate}
        </p>
        <Button color="green" type="solid" label="Manage Subscription" block />
      </div>
    );
  };

  const renderStatus = () => {
    if (
      isPremium &&
      profile?.subscriptions &&
      profile?.subscriptions?.cancelled_at === null
    ) {
      return renderPremium();
    }
    if (
      isPremium &&
      profile?.subscriptions &&
      profile?.subscriptions?.cancelled_at
    ) {
      return renderCancelled();
    }
    return renderBasic();
  };

  return <section className="premium-container">{renderStatus()}</section>;
};
export default PremiumStatus;
